import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Eye,
  Edit,
  Send,
  MoreHorizontal,
  Calendar,
  Tag,
  Receipt,
  Clock,
  User
} from 'lucide-react';
import ExpenseStatusBadge from './ExpenseStatusBadge';
import { Currency } from '@/components/i18n/Currency';
import { LocalDate } from '@/components/i18n/LocalDate';
import expenseService from '@/services/expenseService';
import {
  Expense,
  ExpenseStatus,
  EXPENSE_CATEGORY_INFO
} from '@/types/expense';

interface MobileExpenseCardProps {
  expense: Expense;
  onView?: (expense: Expense) => void;
  onEdit?: (expense: Expense) => void;
  onSubmit?: (expense: Expense) => void;
  showActions?: boolean;
  className?: string;
}

const MobileExpenseCard: React.FC<MobileExpenseCardProps> = ({
  expense,
  onView,
  onEdit,
  onSubmit,
  showActions = true,
  className
}) => {
  const categoryInfo = EXPENSE_CATEGORY_INFO[expense.category];





  const formatDateTime = (dateString: string) => {
    return expenseService.formatDateTime(dateString);
  };

  const canSubmit = (expense: Expense) => {
    return expense.status === ExpenseStatus.DRAFT;
  };

  const canEdit = (expense: Expense) => {
    return expense.status === ExpenseStatus.DRAFT || 
           expense.status === ExpenseStatus.REJECTED;
  };

  return (
    <Card className={`transition-shadow hover:shadow-md ${className}`} data-testid={`expense-item-${expense.id}`}>
      <CardContent className="p-4">
        <div className="space-y-3">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="flex-shrink-0">
                <span className="text-xl">{categoryInfo.icon}</span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-base leading-tight truncate">
                  {expense.description}
                </h3>
                <div className="text-sm text-muted-foreground">
                  {categoryInfo.label}
                </div>
              </div>
            </div>
            <div className="flex-shrink-0 text-right">
              <div className="text-lg font-bold text-primary">
                <Currency amount={parseFloat(expense.amount)} />
              </div>
              <ExpenseStatusBadge status={expense.status} size="sm" />
            </div>
          </div>

          {/* Details */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="h-4 w-4 flex-shrink-0" />
                              <LocalDate date={expense.expenseDate} />
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Receipt className="h-4 w-4 flex-shrink-0" />
              <span>ID: {expense.id.slice(-8)}</span>
            </div>
          </div>

          {/* Timestamps */}
          <div className="space-y-1 text-xs text-muted-foreground">
            {expense.createdAt && (
              <div className="flex items-center gap-2">
                <Clock className="h-3 w-3" />
                <span>Created {formatDateTime(expense.createdAt)}</span>
              </div>
            )}
            {expense.submittedAt && (
              <div className="flex items-center gap-2">
                <Send className="h-3 w-3" />
                <span>Submitted {formatDateTime(expense.submittedAt)}</span>
              </div>
            )}
            {expense.reviewedAt && (
              <div className="flex items-center gap-2">
                <User className="h-3 w-3" />
                <span>Reviewed {formatDateTime(expense.reviewedAt)}</span>
              </div>
            )}
          </div>

          {/* Notes */}
          {expense.notes && (
            <div className="text-sm bg-muted/50 p-2 rounded">
              <div className="text-xs text-muted-foreground mb-1">Notes:</div>
              <div className="line-clamp-2">{expense.notes}</div>
            </div>
          )}

          {/* Rejection Reason */}
          {expense.status === ExpenseStatus.REJECTED && expense.rejectionReason && (
            <div className="text-sm bg-red-50 border border-red-200 p-2 rounded">
              <div className="text-xs text-red-600 font-medium mb-1">Rejected:</div>
              <div className="text-red-700 line-clamp-2">{expense.rejectionReason}</div>
            </div>
          )}

          {/* Actions */}
          {showActions && (
            <div className="flex items-center justify-between pt-2 border-t">
              <div className="flex items-center gap-2">
                {onView && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onView(expense)}
                    className="gap-2 text-xs"
                  >
                    <Eye className="h-3 w-3" />
                    View
                  </Button>
                )}
                
                {canEdit(expense) && onEdit && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onEdit(expense)}
                    className="gap-2 text-xs"
                  >
                    <Edit className="h-3 w-3" />
                    Edit
                  </Button>
                )}

                {canSubmit(expense) && onSubmit && (
                  <Button
                    size="sm"
                    onClick={() => onSubmit(expense)}
                    className="gap-2 text-xs"
                  >
                    <Send className="h-3 w-3" />
                    Submit
                  </Button>
                )}
              </div>

              <DropdownMenu>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>More actions</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <DropdownMenuContent align="end">
                  {onView && (
                    <DropdownMenuItem onClick={() => onView(expense)}>
                      <Eye className="mr-2 h-4 w-4" />
                      View Details
                    </DropdownMenuItem>
                  )}
                  {canEdit(expense) && onEdit && (
                    <DropdownMenuItem onClick={() => onEdit(expense)}>
                      <Edit className="mr-2 h-4 w-4" />
                      Edit Expense
                    </DropdownMenuItem>
                  )}
                  {canSubmit(expense) && onSubmit && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => onSubmit(expense)}>
                        <Send className="mr-2 h-4 w-4" />
                        Submit for Approval
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem disabled>
                    <Receipt className="mr-2 h-4 w-4" />
                    ID: {expense.id}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default MobileExpenseCard; 